import React from 'react';
import { MapPinIcon, CurrencyDollarIcon, BuildingOffice2Icon, BriefcaseIcon } from '@heroicons/react/24/solid';
import type { IntlShape } from 'react-intl';

export interface FilterCategory {
  id: string;
  label: string;
  icon?: React.ReactNode;
  colorClass: string;
  hasFilters: boolean;
}

export interface FilterCategoryButtonsProps {
  intl?: IntlShape;
  categories: FilterCategory[];
  activeCategory: string | null;
  onCategoryClick: (categoryId: string) => void;
  className?: string;
}

/**
 * Filter category buttons component
 * Shows category buttons (Location, Salary, Company, Job) that open filter modals
 * Designed with Apple design language: subtle shadows, smooth transitions, clean aesthetics
 */
export const FilterCategoryButtons: React.FC<FilterCategoryButtonsProps> = ({
  categories,
  activeCategory,
  onCategoryClick,
  className = '',
  intl: _intl,
}) => {
  return (
    <div className={`flex items-center gap-2 flex-wrap ${className}`}>
      {categories.map((category) => {
        const isActive = activeCategory === category.id;
        const hasFilters = category.hasFilters;
        
        // Apple-style button classes with optimized CSS
        const baseClasses = 'flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium transition-all duration-300 ease-out cursor-pointer select-none border';
        
        let buttonClasses = baseClasses;
        let shadowStyle: React.CSSProperties = {};
        
        if (isActive) {
          // Active state: colored background with stronger shadow and border
          buttonClasses += ` ${category.colorClass} border-2`;
          shadowStyle = {
            boxShadow: '0 2px 8px 0 rgba(0, 0, 0, 0.12), 0 1px 3px 0 rgba(0, 0, 0, 0.08)',
          };
        } else if (hasFilters) {
          // Has filters: colored background with visible border and shadow
          buttonClasses += ` ${category.colorClass} border`;
          shadowStyle = {
            boxShadow: '0 1px 4px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.06)',
          };
        } else {
          // Default: white/light background with visible border and shadow for contrast
          buttonClasses += ' bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600';
          shadowStyle = {
            boxShadow: '0 1px 4px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.06)',
          };
        }
        
        // Add hover effects for better interactivity feedback
        buttonClasses += ' hover:shadow-md hover:scale-[1.02] active:scale-[0.98]';
        
        return (
          <button
            key={category.id}
            onClick={() => onCategoryClick(category.id)}
            className={buttonClasses}
            aria-label={category.label}
            aria-pressed={isActive}
            style={{
              ...shadowStyle,
              // Smooth transform origin
              transformOrigin: 'center',
              // Subtle backdrop blur for glassmorphism effect when active
              backdropFilter: isActive ? 'blur(10px) saturate(180%)' : 'none',
              WebkitBackdropFilter: isActive ? 'blur(10px) saturate(180%)' : 'none',
            }}
          >
            <span className="flex-shrink-0" style={{ 
              opacity: isActive ? 1 : 0.9,
              transition: 'opacity 0.2s ease-out'
            }}>
              {category.icon}
            </span>
            <span className="whitespace-nowrap font-semibold">{category.label}</span>
            {hasFilters && (
              <span 
                className="ml-0.5 text-[10px] leading-none"
                style={{
                  opacity: isActive ? 0.9 : 0.7,
                  transition: 'opacity 0.2s ease-out'
                }}
                aria-hidden="true"
              >
                ●
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
};

export interface FilterCategoryLabels {
  location: string;
  salary: string;
  company: string;
  job: string;
}

/**
 * Creates filter categories with pre-generated labels (for static SRP without IntlProvider).
 */
export function createFilterCategoriesFromLabels(
  labels: FilterCategoryLabels,
  filtersObj: Record<string, string[]>
): FilterCategory[] {
  return [
    {
      id: 'location',
      label: labels.location,
      icon: <MapPinIcon className="w-4 h-4" />,
      colorClass: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 border-green-300 dark:border-green-700',
      hasFilters: !!(filtersObj.location?.length || filtersObj.remoteness?.length),
    },
    {
      id: 'salary',
      label: labels.salary,
      icon: <CurrencyDollarIcon className="w-4 h-4" />,
      colorClass: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-300 dark:border-blue-700',
      hasFilters: !!(filtersObj.salaryRangeMin?.length || filtersObj.vacationWeeks?.length),
    },
    {
      id: 'company',
      label: labels.company,
      icon: <BuildingOffice2Icon className="w-4 h-4" />,
      colorClass: 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 border-purple-300 dark:border-purple-700',
      hasFilters: !!(filtersObj.companyId?.length || filtersObj.companyIndustry?.length),
    },
    {
      id: 'job',
      label: labels.job,
      icon: <BriefcaseIcon className="w-4 h-4" />,
      colorClass: 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 border-orange-300 dark:border-orange-700',
      hasFilters: !!(
        filtersObj.jobType?.length ||
        filtersObj.normalisedRoleLevel?.length ||
        filtersObj.requiredLanguages?.length ||
        filtersObj.benefits?.length ||
        filtersObj.skillsAndTechnologies?.length ||
        filtersObj.field?.length ||
        filtersObj.educationLevel?.length ||
        filtersObj.peopleManagementTier?.length ||
        filtersObj.lastSeenMin?.length ||
        filtersObj.excludeSaved?.length ||
        filtersObj.excludeViewed?.length ||
        filtersObj.excludeApplied?.length
      ),
    },
  ];
}

/**
 * Helper function to create filter categories array
 */
export function createFilterCategories(
  intl: IntlShape,
  filtersObj: Record<string, string[]>
): FilterCategory[] {
  return createFilterCategoriesFromLabels(
    {
      location: intl.formatMessage({ id: 'search.refinements.location', defaultMessage: 'Location', description: 'Filter label for location selection in search refinements' }),
      salary: intl.formatMessage({ id: 'search.refinements.salary', defaultMessage: 'Salary', description: 'Filter label for salary range selection in search refinements' }),
      company: intl.formatMessage({ id: 'search.refinements.company', defaultMessage: 'Company', description: 'Filter label for company selection in search refinements' }),
      job: intl.formatMessage({ id: 'search.refinements.job', defaultMessage: 'Job', description: 'Filter label for job-related filters (type, title, etc.) in search refinements' }),
    },
    filtersObj
  );
}
